/**
 * Custom CKEditor Plugin: FontBackgroundColorBlock
 *
 * Überschreibt das Standard-Verhalten von FontBackgroundColor:
 * Statt die background-color in einen neuen <span> innerhalb des Elements zu setzen,
 * wird die background-color direkt auf das Block-Element (z.B. <p>, <h2>, etc.) gesetzt.
 */

export function FontBackgroundColorBlock(editor) {
  const schema = editor.model.schema;

  // Erlaube das Attribut 'fontBackgroundColor' auf allen Block-Elementen
  schema.extend("$block", { allowAttributes: "fontBackgroundColor" });

  // Erlaube das Attribut auch auf Container-Elementen (z.B. blockQuote, etc.)
  const containerElements = ["blockQuote", "table", "tableCell", "tableRow"];
  for (const elName of containerElements) {
    if (schema.isRegistered(elName)) {
      schema.extend(elName, { allowAttributes: "fontBackgroundColor" });
    }
  }

  // Dynamische Attribut-Prüfung: Erlaube fontBackgroundColor auf allen
  // Nicht-Root-Elementen, damit auch spät registrierte Elemente unterstützt werden
  schema.addAttributeCheck((context, attributeName) => {
    if (attributeName === "fontBackgroundColor") {
      const lastItem = context.last;
      if (lastItem && lastItem.is && !lastItem.is("rootElement")) {
        return true;
      }
    }
  });

  // Downcast: Model -> View (Editing & Data)
  editor.conversion.for("downcast").add(dispatcher => {
    dispatcher.on(
      "attribute:fontBackgroundColor",
      (evt, data, conversionApi) => {
        const { item, attributeNewValue, attributeOldValue } = data;
        const { mapper, writer } = conversionApi;

        // Nur Block-Elemente behandeln (nicht Inline-Text)
        if (!item.is("element")) {
          return;
        }

        // Prüfe ob das Event konsumierbar ist
        if (!conversionApi.consumable.consume(data.item, evt.name)) {
          return;
        }

        // Das View-Element für das Model-Element holen
        let viewElement = mapper.toViewElement(item);
        if (!viewElement) {
          return;
        }

        // Alten Wert entfernen
        if (attributeOldValue) {
          writer.removeStyle("background-color", viewElement);
        }

        // Neuen Wert setzen
        if (attributeNewValue) {
          writer.setStyle("background-color", attributeNewValue, viewElement);
        }
      },
      { priority: "highest" }
    );
  });

  // Upcast: View -> Model (beim Laden von Daten)
  editor.conversion.for("upcast").add(dispatcher => {
    dispatcher.on(
      "element",
      (evt, data, conversionApi) => {
        const viewElement = data.viewItem;

        // Nur Block-Elemente mit background-color Style verarbeiten
        if (!viewElement.is("element")) {
          return;
        }

        const bgColor = viewElement.getStyle("background-color");
        if (!bgColor) {
          return;
        }

        // Prüfen ob es ein Block-Element ist (p, h1-h6, blockquote, li, etc.)
        const blockElements = [
          "p",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "blockquote",
          "li",
          "div",
          "td",
          "th"
        ];
        if (!blockElements.includes(viewElement.name)) {
          return;
        }

        // Das Model-Element nach der Standard-Konvertierung holen
        if (!data.modelRange) {
          return;
        }

        const modelElement =
          data.modelRange.start.nodeAfter || data.modelRange.start.parent;
        if (!modelElement || !modelElement.is("element")) {
          return;
        }

        // Prüfen ob das Attribut gesetzt werden darf
        if (
          !conversionApi.schema.checkAttribute(
            modelElement,
            "fontBackgroundColor"
          )
        ) {
          return;
        }

        conversionApi.writer.setAttribute(
          "fontBackgroundColor",
          bgColor,
          modelElement
        );
      },
      { priority: "low" }
    );
  });

  // Command überschreiben: Wenn der fontBackgroundColor-Command ausgeführt wird,
  // prüfe ob die gesamte Block-Länge ausgewählt ist oder nur ein Teil.
  const originalCommand = editor.commands.get("fontBackgroundColor");
  const originalExecute = originalCommand.execute.bind(originalCommand);

  originalCommand.execute = function(options) {
    const model = editor.model;
    const document = model.document;
    const selection = document.selection;
    const value = options && options.value ? options.value : null;

    // Prüfe ob es eine Textauswahl gibt (nicht nur Cursor)
    const range = selection.getFirstRange();
    if (range && !range.isCollapsed) {
      const blocks = Array.from(selection.getSelectedBlocks());

      // Prüfe für jeden Block, ob die Auswahl den gesamten Text umfasst
      let allBlocksFullySelected = true;

      for (const block of blocks) {
        // Textlänge des Blocks ermitteln
        let blockTextLength = 0;
        for (const child of block.getChildren()) {
          if (child.is("$text") || child.is("$textProxy")) {
            blockTextLength += child.data.length;
          }
        }

        // Auswahl-Länge innerhalb dieses Blocks ermitteln
        const blockRange = model.createRangeIn(block);
        const intersection = range.getIntersection(blockRange);

        if (!intersection) {
          allBlocksFullySelected = false;
          break;
        }

        let selectedTextLength = 0;
        for (const item of intersection.getItems()) {
          if (item.is("$text") || item.is("$textProxy")) {
            selectedTextLength += item.data.length;
          }
        }

        // Teilauswahl: 1 bis n-1 Zeichen ausgewählt
        if (selectedTextLength > 0 && selectedTextLength < blockTextLength) {
          allBlocksFullySelected = false;
          break;
        }
      }

      // Bei Teilauswahl: Standard-Inline-Verhalten verwenden (span)
      if (!allBlocksFullySelected) {
        originalExecute(options);
        return;
      }
    }

    // Bei vollständiger Auswahl oder Cursor: auf Block-Element setzen
    // Falls ein Elternelement vorhanden ist, auf dieses setzen
    model.change(writer => {
      const blocks = Array.from(selection.getSelectedBlocks());
      const processed = new Set();

      for (const block of blocks) {
        // Gehe auf das Elternelement, falls vorhanden
        const target = getParentBlockElement(block) || block;

        // Vermeide doppelte Verarbeitung desselben Elternelements
        if (processed.has(target)) {
          continue;
        }
        processed.add(target);

        if (value) {
          writer.setAttribute("fontBackgroundColor", value, target);
        } else {
          writer.removeAttribute("fontBackgroundColor", target);
        }
      }
    });
  };
}

/**
 * Gibt das Elternelement eines Block-Elements zurück, falls vorhanden.
 * Geht nicht über das Root-Element ($root) hinaus.
 */
function getParentBlockElement(element) {
  const parent = element.parent;
  if (parent && parent.is("element") && !parent.is("rootElement")) {
    return parent;
  }
  return null;
}
